/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */

import React, { useState, useEffect, useContext } from 'react';
import { Pagination, PaginationItem, PaginationLink, Form, FormGroup, Input, InputGroup, InputGroupAddon, Collapse, Badge, Label, Button, Modal, ModalHeader, ModalBody, ModalFooter, Table } from 'reactstrap';
import Alert from 'react-s-alert';

// context imports
import { ThemeContext } from '../../contexts/ThemeContext';
import { AuthContext } from '../../contexts/AuthContext';

// service imports
import AdminService from '../../services/AdminService';
import GeneralService from '../../services/GeneralService';

// local imports
import ErrorBoundary from '../general/ErrorBoundary';

const ProviderFormResponse = props => {

    // variables
    let timeout = 0;

    // states
    const [isLoading, setIsLoading] = useState(false);
    const [formName, setFormName] = useState("");
    const [formResponses, setFormResponses] = useState([]);
    const [selectedResponseIndex, setSelectedResponseIndex] = useState(-1);
    const [expandAll, setExpandAll] = useState(false);
    const [userData, setUserData] = useState({ id: "", name: "", profileImage: "" });
    const [pagination, setPagination] = useState({ limit: 10, page: 1, searchVal: '' });

    // contexts
    const { theme, colors } = useContext(ThemeContext);

    // useEffect
    useEffect(() => {
        if (props.selectedForm.id) {
            getProviderFormResponses();
        } else {
            props.gotoForms();
        }
    }, []);

    useEffect(() => {
        getProviderFormResponses();
    }, [pagination.page, pagination.limit, pagination.searchVal]);

    const getProviderFormResponses = () => {
        if (!isLoading) {
            setIsLoading(true);
            AdminService.getProviderFormResponses(props.selectedForm.id, pagination.limit, pagination.page, pagination.searchVal)
                .then(data => {
                    try {
                        // console.log("data", data);
                        setFormName(data.responses.length ? data.responses[0].formName : '');
                        setFormResponses(data.responses);
                        if (data.pagination.totalPages <= data.pagination.page) {
                            data.pagination.page = data.pagination.totalPages;
                        }
                        setPagination(data.pagination);
                        setIsLoading(false);
                    } catch (err) {
                        Alert.error("Some error occured, please try again later or report support team.", {
                            position: 'top-right',
                            effect: 'slide'
                        });
                        setIsLoading(false);
                        GeneralService.logError('at Provider ProviderFormResponse useEffect', 'Error in try catch block', err.stack);
                    }
                });
        }
    }

    const goToForms = () => {
        setFormName("");
        setFormResponses([]);
        // setModal({ isVisible: false, isUpdate: false, formName: "", data: { form: "", id: "", type: "", question: "", answers: [], orderNo: "" } });
        // setConfirmationModal({ isVisible: false, id: "" });
        setPagination({ limit: 10, page: 1 });
        props.gotoForms();
    }

    const handleSearch = e => {
        try {
            let value = e.target.value;
            if (timeout) clearTimeout(timeout);
            timeout = setTimeout(() => {
                setPagination({ ...pagination, searchVal: value });
            }, 500);
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin FormQuestions handleSerach', 'Error in try catch block', err.stack);
        }
    }

    const handlePagination = (e, index) => {
        e.preventDefault();
        try {
            setPagination({ ...pagination, page: index });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin FormQuestions handlePagination', 'Error in try catch block', err.stack);
        }
    }

    const toggle = (index) => {
        try {
            const tempMainArray = [...formResponses];
            tempMainArray[selectedResponseIndex].response[index].isOpen = !tempMainArray[selectedResponseIndex].response[index].isOpen;
            setFormResponses(tempMainArray);
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Provider FAQ toggle', 'Error in try catch block', err.stack);
        }
    };

    const handleExpanAll = () => {
        const tempMainArray = [...formResponses];
        if (expandAll) {
            tempMainArray[selectedResponseIndex].response.map(item => item.isOpen = false);
        } else {
            tempMainArray[selectedResponseIndex].response.map(item => item.isOpen = true);
        }
        setExpandAll(!expandAll);
        setFormResponses(tempMainArray);
    }

    return (
        <div>
            <div className="p-3">
                {
                    selectedResponseIndex === -1 ?
                        <div>
                            <div className="d-flex my-4">
                                <h3 style={{ marginRight: "15px", cursor: "pointer" }} onClick={() => goToForms()}><i className="fas fa-arrow-left"></i></h3>
                                <h3>{formName}</h3>
                            </div>
                            <div className="p-3 mt-3" style={theme.card}>
                                <hr />
                                <div className="table-responsive">
                                    <table className="table table-bordered mb-0" id="dataTable" width="100%" cellSpacing="0">
                                        <thead>
                                            <tr>
                                                <td colSpan="6" className="border-0 px-0">
                                                    <div className="d-flex">
                                                        <div className="ml-auto">
                                                            Search<input type="text" id="searchInput" className="ml-2" style={{ marginRight: "-20px", paddingRight: "20px" }} onChange={handleSearch}></input>
                                                            <span style={{ cursor: "pointer", marginRight: "7px", alignSelf: "center", zIndex: 100 }} onClick={() => { document.getElementById("searchInput").value = ""; setPagination({ ...pagination, searchVal: "" }); }}><i className="fa fa-times"></i></span>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>Provider Name</th>
                                                <th>Provider Email</th>
                                                <th align="center" style={{ textAlign: "center" }} colSpan="5">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                formResponses.map((item, index) => (
                                                    <ErrorBoundary errorAt={`At Admin Forms Table${index}`}>
                                                        <tr key={index}>
                                                            <td>{item.response.find(item => item.type === 'name').userResponse[0]}</td>
                                                            <td>{item.response.find(item => item.type === 'email').userResponse[0]}</td>
                                                            <td align="center" style={{ width: '1px' }}><a style={{ color: "#384E73" }} href="#" onClick={() => setSelectedResponseIndex(index)}>View Response</a></td>
                                                        </tr>
                                                    </ErrorBoundary>
                                                ))
                                            }
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan="6" className="border-0 pb-0 px-0">
                                                    <div className="d-flex">
                                                        <ErrorBoundary errorAt="At Admin Forms Pagination">
                                                            <div className="p-2">Showing {pagination.totalResults == 0 ? 0 : (1 + (10 * (pagination.page - 1)))} to {(pagination.limit + (10 * (pagination.page - 1))) > pagination.totalResults ? pagination.totalResults : (pagination.limit + (10 * (pagination.page - 1)))} of {pagination.totalResults} entries</div>
                                                            <Pagination aria-label="Page navigation example" className="ml-auto">
                                                                <PaginationItem disabled={pagination.page == 1}>
                                                                    <PaginationLink
                                                                        onClick={e => handlePagination(e, pagination.page - 1)}
                                                                        previous
                                                                        href="#" />
                                                                </PaginationItem>
                                                                {[...Array(pagination.totalPages)].map((page, i) =>
                                                                    <PaginationItem active={i + 1 === pagination.page} key={i}>
                                                                        <PaginationLink onClick={e => handlePagination(e, i + 1)} href="#">
                                                                            {i + 1}
                                                                        </PaginationLink>
                                                                    </PaginationItem>
                                                                )}
                                                                <PaginationItem disabled={pagination.page == pagination.totalPages}>
                                                                    <PaginationLink
                                                                        onClick={e => handlePagination(e, pagination.page + 1)}
                                                                        next
                                                                        href="#" />
                                                                </PaginationItem>
                                                            </Pagination>
                                                        </ErrorBoundary>
                                                    </div>
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        </div>
                        :
                        <div>
                            <div className="d-flex my-4">
                                <h3 style={{ marginRight: "15px", cursor: "pointer" }} onClick={() => setSelectedResponseIndex(-1)}><i className="fas fa-arrow-left"></i></h3>
                                <h3>{formResponses[selectedResponseIndex].form}</h3>
                                <h5 className="ml-auto" style={{ cursor: "pointer" }} onClick={handleExpanAll}>{expandAll ? "Collapse all" : "Expand all"}</h5>
                            </div>
                            {
                                formResponses[selectedResponseIndex].response.map((item, index) => (
                                    <div key={index} className="p-3 my-3" style={theme.card}>
                                        <ErrorBoundary errorAt={`At ProviderFormResponse FormResponse${index}`}>
                                            <div className="d-flex" style={{ cursor: "pointer" }} onClick={() => toggle(index)}>
                                                <Badge style={{ backgroundColor: colors.primary, marginRight: "15px", height: "fit-content" }}>{index + 1}</Badge>
                                                <span style={theme.headingBold}>{item.question}</span>
                                                <span className="ml-auto" style={{ marginLeft: "15px" }}>
                                                    {
                                                        formResponses[selectedResponseIndex].response[index].isOpen
                                                            ? <i className="fas fa-angle-up"></i>
                                                            : <i className="fas fa-angle-down"></i>
                                                    }
                                                </span>
                                            </div>

                                            <Collapse isOpen={formResponses[selectedResponseIndex].response[index].isOpen}>
                                                <hr />
                                                <Table className="mb-0" borderless>
                                                    <tbody>
                                                        {
                                                            item.type === 'email' || item.type === 'name' || item.type === 'shortText' || item.type === 'longText' ?
                                                                <tr><td><span style={{ whiteSpace: 'pre' }}>{item.userResponse[0]}</span></td></tr>
                                                                :
                                                                item.answers.map((item2, index) => (
                                                                    <tr key={index} style={item.userResponse.includes(item2) ? { fontWeight: "bold" } : {}}>
                                                                        <td style={{ padding: "0 0 0 8px" }}><span>{item2}{item.userResponse.includes(item2) ? <i style={{ color: "#009a34" }} className="fas fa-check ml-2"></i> : null}</span></td>
                                                                    </tr>
                                                                ))
                                                        }
                                                    </tbody>
                                                </Table>
                                            </Collapse>
                                        </ErrorBoundary>
                                    </div>
                                ))
                            }
                        </div>
                }
            </div>
        </div>
    )

}

export default ProviderFormResponse;